import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import { API_URL, apiFetch } from './api/client';
import { TableSortContext } from './context/TableSortContext';
import { AuthProvider } from './context/AuthContext';
import { AppLayout } from './components/AppLayout';
import { LoginPage } from './components/LoginPage';
import { useTableSort } from './hooks/useTableSort';

function loginPathWithRedirect(returnPath) {
  const path = returnPath || '/';
  if (path === '/' || path === '/login' || path.startsWith('/login?')) {
    return '/login';
  }
  return `/login?redirect=${encodeURIComponent(path)}`;
}

function safeRedirectTarget(value) {
  if (!value || !value.startsWith('/') || value.startsWith('/login')) {
    return '/dashboard';
  }
  return value;
}

function AuthenticatedApp({ auth, setAuth }) {
  const navigate = useNavigate();
  const { tableSort, requestSort, sortData } = useTableSort();

  const handleLogout = async () => {
    try {
      await apiFetch(`${API_URL}/logout`, { method: 'POST' });
    } catch {
      /* ignore */
    }
    setAuth(null);
    navigate('/login', { replace: true });
  };

  const refreshAuth = async () => {
    try {
      const r = await apiFetch(`${API_URL}/auth/me`);
      const j = await r.json();
      if (j.authenticated) setAuth(j);
    } catch {
      /* ignore */
    }
  };

  return (
    <AuthProvider auth={auth} setAuth={setAuth}>
      <TableSortContext.Provider value={{ tableSort, requestSort, sortData }}>
        <Routes>
          <Route
            path="/*"
            element={
              <AppLayout
                onLogout={() => void handleLogout()}
                onAuthRefresh={() => void refreshAuth()}
              />
            }
          />
        </Routes>
      </TableSortContext.Provider>
    </AuthProvider>
  );
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [auth, setAuth] = useState(undefined);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch(`${API_URL}/auth/me`);
        const j = await r.json();
        if (!cancelled) setAuth(j.authenticated ? j : null);
      } catch {
        if (!cancelled) setAuth(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const goToLogin = useCallback(
    (returnPath) => {
      navigate(loginPathWithRedirect(returnPath), { replace: true });
    },
    [navigate]
  );

  useEffect(() => {
    const onLost = () => {
      setAuth(null);
      const returnPath = `${location.pathname}${location.search}`;
      if (!returnPath.startsWith('/login')) {
        goToLogin(returnPath);
      }
    };
    window.addEventListener('marketing-auth-lost', onLost);
    return () => window.removeEventListener('marketing-auth-lost', onLost);
  }, [location.pathname, location.search, goToLogin]);

  if (auth === undefined) {
    return (
      <div className="login-screen">
        <p style={{ color: '#94a3b8' }}>Oturum kontrol ediliyor…</p>
      </div>
    );
  }

  const redirectParam = new URLSearchParams(location.search).get('redirect');
  const loginRedirectTo = safeRedirectTarget(redirectParam);

  return (
    <Routes>
      <Route
        path="/login"
        element={
          auth ? (
            <Navigate to={loginRedirectTo} replace />
          ) : (
            <LoginPage onLoggedIn={setAuth} redirectTo={loginRedirectTo} />
          )
        }
      />
      <Route path="/" element={<Navigate to={auth ? '/dashboard' : '/login'} replace />} />
      <Route
        path="/*"
        element={
          auth ? (
            <AuthenticatedApp auth={auth} setAuth={setAuth} />
          ) : (
            <Navigate
              to={loginPathWithRedirect(`${location.pathname}${location.search}`)}
              replace
            />
          )
        }
      />
    </Routes>
  );
}

export default function AppWithRouter() {
  return (
    <BrowserRouter>
      <App />
    </BrowserRouter>
  );
}
